import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import type { PostTimeOff, PostTimeOffTypeRate } from "@/server-types";

export type CompensationMode = PostTimeOffTypeRate["body"]["mode"];

/** What the server returns for a rate. */
export type CompensationRate = {
  mode: CompensationMode;
  percent: number | null;
  flatAmountCents: number | null;
};

/** What the server takes for a rate: one shape per mode. */
export type CompensationRateBody = NonNullable<
  PostTimeOff["body"]["compensation"]
>;

/** What the form holds while a rate is being typed; amounts stay strings until saved. */
export type CompensationDraft = {
  mode: CompensationMode;
  percent: string;
  flatAmount: string;
};

export function emptyCompensationDraft(): CompensationDraft {
  return { mode: "percentOfHourlyCost", percent: "100", flatAmount: "" };
}

export function draftFromRate(
  rate: CompensationRate | null,
): CompensationDraft {
  if (!rate) return emptyCompensationDraft();
  return {
    mode: rate.mode,
    percent: rate.percent === null ? "100" : String(rate.percent),
    flatAmount:
      rate.flatAmountCents === null
        ? ""
        : (rate.flatAmountCents / 100).toFixed(2).replace(".", ","),
  };
}

/** Null when the draft does not describe a rate the server would accept. */
export function rateFromDraft(
  draft: CompensationDraft,
): CompensationRateBody | null {
  if (draft.mode === "none") {
    return { mode: "none", percent: null, flatAmountCents: null };
  }

  if (draft.mode === "percentOfHourlyCost") {
    const percent = parseDecimal(draft.percent);
    if (percent === null || percent > 100) return null;
    return {
      mode: "percentOfHourlyCost",
      percent: Math.round(percent * 100) / 100,
      flatAmountCents: null,
    };
  }

  const amount = parseDecimal(draft.flatAmount);
  if (amount === null) return null;
  return {
    mode: "flatPerDay",
    percent: null,
    flatAmountCents: Math.round(amount * 100),
  };
}

export function formatCompensation(
  rate: CompensationRate | null,
  currency = "EUR",
): string {
  if (!rate || rate.mode === "none") return t`Šteje kot delovni čas`;

  if (rate.mode === "percentOfHourlyCost") {
    const percent = rate.percent ?? 0;
    return t`${percent} % urnega stroška`;
  }

  const amount = ((rate.flatAmountCents ?? 0) / 100).toLocaleString(
    i18n.locale || undefined,
    { style: "currency", currency },
  );
  return t`${amount} na dan, poleg delovnega časa`;
}

/** What a time off's "Nadomestilo" select holds; `inherit` follows the type. */
export type CompensationChoice =
  | "inherit"
  | `percent:${number}`
  | "customPercent"
  | "flatPerDay";

/** 100 % and unpaid, plus every percentage the salon's own types use, highest first. */
export function presetPercents(
  typePercents: readonly (number | null | undefined)[],
): number[] {
  const used = typePercents.filter(
    (percent): percent is number => percent != null,
  );
  return [...new Set([100, ...used, 0])].sort((a, b) => b - a);
}

/** The choice a stored override opens as. */
export function choiceFromRate(
  rate: CompensationRate | null,
): CompensationChoice {
  if (!rate) return "inherit";
  if (rate.mode === "flatPerDay") return "flatPerDay";
  if (rate.mode === "none") return "percent:100";
  return `percent:${rate.percent ?? 100}`;
}

/** Picking a choice rewrites only the part of the draft that choice owns. */
export function draftForChoice(
  choice: CompensationChoice,
  draft: CompensationDraft,
): CompensationDraft {
  if (choice === "flatPerDay") return { ...draft, mode: "flatPerDay" };
  if (choice === "customPercent") {
    return { ...draft, mode: "percentOfHourlyCost", percent: "" };
  }
  if (choice === "inherit") return draft;
  return {
    ...draft,
    mode: "percentOfHourlyCost",
    percent: choice.slice("percent:".length),
  };
}

/** Null for `inherit`; otherwise the rate the draft describes, or undefined when it cannot be read. */
export function rateForChoice(
  choice: CompensationChoice,
  draft: CompensationDraft,
): CompensationRateBody | null | undefined {
  if (choice === "inherit") return null;
  return rateFromDraft(draft) ?? undefined;
}

function parseDecimal(input: string): number | null {
  const normalized = input.trim().replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}
